import React, { useEffect, useRef, useState } from 'react'
import { Redirect } from 'react-router-dom'
import { arrayOf, bool, func, object } from 'prop-types'
import { useFormikContext } from 'formik'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/pro-regular-svg-icons/faTrash'
import { faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons/faExclamationTriangle'
import { toast } from 'react-toastify'
import { DELETE, useClient } from '@peracto/client'
import { Checkbox, Form, FormActions, Group, Input, Modal, Select, Textarea } from '@peracto/peracto-ui'

import { EMPTY_CATEGORY, defaultSlug } from './categoryFields'
import { MAX_DEPTH, flatten, parentOptions } from './categoryTree'
import { showApiError } from './apiErrors'

const SlugFollowsName = ({ parents, autoSlug }) => {
    const { values, setFieldValue } = useFormikContext()
    const parentSlug = (parents.find(parent => parent['@id'] === values.parent) || {}).slug

    useEffect(() => {
        if (autoSlug.current !== null && values.slug === autoSlug.current) {
            autoSlug.current = defaultSlug(values.name, parentSlug)
            setFieldValue('slug', autoSlug.current, false)
        }
    }, [values.name, values.slug, parentSlug, autoSlug, setFieldValue])

    return null
}

const CategoryFields = ({ parents, autoSlug }) => (
    <>
        <SlugFollowsName parents={parents} autoSlug={autoSlug} />
        <Group name="Category" id="category">
            <Input name="name" label="Name" required testId="vehicle-category-name" />
            <Select
                name="parent"
                label="Parent category"
                help={`Leave blank for a top-level category. Categories go ${MAX_DEPTH} levels deep at most, so only parents that keep this one and those under it within that are offered.`}
                options={[
                    { label: 'None (top level)', value: '' },
                    ...parents.map(parent => ({ label: parent.path.join(' › '), value: parent['@id'] })),
                ]}
                testId="vehicle-category-parent"
            />
            <Input
                name="slug"
                label="Slug"
                help="The category's web address. Filled in from the names until you change it; changing it on a live category breaks links to it."
                required
                testId="vehicle-category-slug"
            />
            <Input
                name="position"
                type="number"
                label="Position"
                help="Lower numbers show first among categories at the same level."
            />
            <Input
                name="image"
                label="Image"
                help="The storefront's image name for this category, if it has one."
            />
            <Checkbox
                name="active"
                label="Active"
                help="Switched-off categories aren't shown on the storefront."
            />
        </Group>
        <Group name="Review" id="review">
            <Checkbox
                name="needsReview"
                label="Needs review"
                help="Marks the category for checking, e.g. a TecDoc node the specification left to verify. It still shows on the storefront if it has parts."
            />
            <Textarea name="reviewNote" label="Review note" help="What needs checking, or what was decided." />
        </Group>
    </>
)

const CategoryForm = ({ data, tree, modeEdit = false, onSubmit, schema }) => {
    const { client } = useClient()
    const autoSlug = useRef(modeEdit ? null : '')
    const [redirect, setRedirect] = useState('')
    const [showDeleteDialog, setShowDeleteDialog] = useState(false)
    const self = modeEdit ? flatten(tree).find(node => node['@id'] === data['@id']) : null
    const hasChildren = !!self && self.children.length > 0
    const parents = parentOptions(tree, data)

    const onDelete = async () => {
        try {
            await client(DELETE, 'vehicle-categories', { id: data['@id'] })
            toast.success('Category deleted.')
            setRedirect('/vehicle-categories')
        } catch (e) {
            showApiError(e)
        }
    }

    if (redirect) {
        return <Redirect to={redirect} />
    }

    return (
        <>
            <FormActions>
                {modeEdit && (
                    <button
                        type="button"
                        className="btn btn--outline-primary text-danger"
                        onClick={() => setShowDeleteDialog(true)}
                    >
                        <FontAwesomeIcon icon={faTrash} className="mr-2" />
                        Delete Category
                    </button>
                )}
            </FormActions>

            <Form
                values={{ ...EMPTY_CATEGORY, ...data }}
                schema={schema}
                onSubmit={onSubmit}
                autoComplete="off"
            >
                <CategoryFields parents={parents} autoSlug={autoSlug} />
            </Form>

            {modeEdit && (
                <Modal
                    isVisible={showDeleteDialog}
                    title="Delete Category"
                    close={() => setShowDeleteDialog(false)}
                    buttons={[
                        {
                            type: 'btn-outline-secondary',
                            text: 'Close',
                            action: () => setShowDeleteDialog(false),
                        },
                        { type: 'btn-danger', text: 'Delete Category', action: onDelete },
                    ]}
                >
                    <FontAwesomeIcon icon={faExclamationTriangle} size="4x" className="d-block mb-4" />
                    Are you sure you want to delete {data.name}?
                    {hasChildren ? ' The categories under it are deleted too.' : ''} To hide it for now, untick
                    Active instead.
                </Modal>
            )}
        </>
    )
}

CategoryForm.propTypes = {
    data: object,
    tree: arrayOf(object).isRequired,
    modeEdit: bool,
    onSubmit: func.isRequired,
    schema: object.isRequired,
}

export default CategoryForm
